import { action, computed, observable } from 'mobx';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import getDataPoint from '../utils/getDataPoint';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { DataLocator } from '../models/configuration/DataLocator';
import { DataStore } from './DataStore';
import getColumnLabel from '../utils/getColumnLabel';
import getColumnLabelFormat from '../utils/getColumnLabelFormat';

export class ColumnStore {
    @observable dataStore: DataStore;

    // Map of column index to selected column group index
    selectedGroupMap = observable.map();

    constructor(dataStore) {
        this.dataStore = dataStore;
    }

    // Generate columns for UI - adds disclamers, dropdown options, and dynamic columns
    @computed
    get columns(): Array<ColumnConfig> {
        const processedColumns = this.dataStore.config.columnConfig.map(
            (column, columnIndex) => {
                const {
                    type,
                    groups = [],
                    columns = [],
                    disclaimerTypes = [],
                } = column as any;
                const label = getColumnLabel(column, this.dataStore);

                if (type === 'dropdown' && groups.length > 0) {
                    return {
                        type: 'dropdown',
                        groups: groups.map(g => ({
                            ...g,
                            type: 'dropdown',
                            columnIndex,
                            options: groups.map((c, i) => ({
                                groupIndex: i,
                                label: c.label,
                            })),
                            columns:
                                g.columns && g.columns.length > 0
                                    ? [].concat(
                                          ...g.columns.map(c => {
                                              if (c.type === 'dynamic') {
                                                  return this.getColumnsFromDataSet(
                                                      c
                                                  );
                                              }

                                              return {
                                                  ...c,
                                                  disclaimers: this.dataStore.getUnlinkedDisclaimers(
                                                      c.disclaimerTypes
                                                  ),
                                              };
                                          })
                                      )
                                    : g.type === 'dynamic'
                                    ? this.getColumnsFromDataSet(g)
                                    : [
                                          {
                                              ...g,
                                              disclaimers: this.dataStore.getUnlinkedDisclaimers(
                                                  g.disclaimerTypes
                                              ),
                                          },
                                      ],
                            disclaimers: this.dataStore.getUnlinkedDisclaimers(
                                g.disclaimerTypes
                            ),
                        })),
                    };
                }

                // Handle group array of columns
                if (type === 'group' && columns.length > 0) {
                    return {
                        ...column,
                        columns: [].concat(
                            ...columns.map(c => {
                                if (c.type === 'dynamic') {
                                    return this.getColumnsFromDataSet(c);
                                }

                                return {
                                    ...c,
                                    disclaimers: this.dataStore.getUnlinkedDisclaimers(
                                        c.disclaimerTypes
                                    ),
                                };
                            })
                        ),
                        disclaimers: this.dataStore.getUnlinkedDisclaimers(
                            disclaimerTypes
                        ),
                    };
                }

                // Handle group dynamic columns
                if (type === 'group' && columns.type === 'dynamic') {
                    return {
                        ...column,
                        label,
                        columns: this.getColumnsFromDataSet(columns),
                        disclaimers: this.dataStore.getUnlinkedDisclaimers(
                            disclaimerTypes
                        ),
                    };
                }

                return {
                    ...column,
                    label,
                    disclaimers: this.dataStore.getUnlinkedDisclaimers(
                        disclaimerTypes
                    ),
                };
            }
        );

        return processedColumns;
    }

    // Generate columns for data set
    getColumnsFromDataSet = (column: ColumnConfig): Array<ColumnConfig> => {
        const { data, disclaimerTypes } = column;
        const label = getColumnLabel(column, this.dataStore);
        const labelFormat = getColumnLabelFormat(column, this.dataStore);

        // Add locale to label format
        const format = {
            ...labelFormat,
            locale: this.dataStore.culture,
        };

        // Get a dataSet with maximum length of values to use for populating columns
        const columnsDataSet = this.dataStore.funds.reduce((acc, fund) => {
            const dataSet = getDataPoint({
                dataStore: this.dataStore,
                dataConfig: data as DataLocator,
                shareClass: fund,
            });

            if (dataSet.length > acc.length) {
                return dataSet;
            }

            return acc;
        }, []);

        // Create an array of columns for length of columnsDataSet
        // Any placeholders used in column labels will be replaced using values from columnsDataSet
        // The data locator for the column is given the index of the current column
        if (Array.isArray(columnsDataSet)) {
            return columnsDataSet.map((values, i) => {
                return {
                    ...column,
                    label: replacePlaceholders({
                        values,
                        format,
                        text: label,
                        dataStore: this.dataStore,
                    }),
                    data: {
                        ...data,
                        sortKey: values.label,
                        index: i,
                    },
                    disclaimers: this.dataStore.getUnlinkedDisclaimers(
                        disclaimerTypes
                    ),
                };
            });
        }

        return [];
    };

    // Sortable columns array for use in sort selector dropdown
    @computed
    get sortableColumns(): Array<ColumnConfig> {
        const sortableColumns = [];
        this.dataStore.config.columnConfig.map(c => {
            const { label, sortable, columns, groups, data } = c as any;

            if (sortable) {
                sortableColumns.push({
                    ...c,
                });
            }

            if (data && data.length > 0) {
                data.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push({
                            ...cc,
                            property: cc.code,
                            label: `${label}`,
                        });
                    }
                });
            }

            if (columns) {
                columns.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push({
                            ...cc,
                            label: `${label}: ${cc.label}`,
                        });
                    }
                });
            }

            if (groups) {
                groups.map(g => {
                    if (g.columns) {
                        g.columns.map(gc => {
                            if (gc.sortable) {
                                sortableColumns.push({
                                    ...gc,
                                    label: `${g.label}: ${gc.label}`,
                                });
                            }
                        });
                    } else {
                        sortableColumns.push({
                            ...g,
                            label: `${g.label}`,
                        });
                    }
                });
            }
        });

        return sortableColumns;
    }

    // Sets the selected group of dropdown columns
    @action
    setSelectedGroup = (columnIndex: string, groupIndex: number) => {
        this.selectedGroupMap.set(columnIndex, groupIndex);
    };
}



// WEBPACK FOOTER //
// ./src/stores/ColumnStore.ts